<template>
    <div class="calc-shell" :data-screen-label="screenLabel">
        <div class="calc-shell__bg" aria-hidden="true" />

        <CalcNav
            :current-step="stepperCurrent"
            :max-reached="stepperMaxReached"
            @navigate="onStepperClick"
            @logo="restart"
            @exit="exit"
        />

        <div class="calc-stage">
            <div v-if="financingUnavailable" class="calc-unavailable" role="alert">
                <h2 class="calc-unavailable__title">{{ $t('§calc.unavailable-title') }}</h2>
                <p class="calc-unavailable__body">{{ $t('§calc.unavailable-body') }}</p>
                <CalcButton
                    variant="primary"
                    label="§calc.unavailable-cta_book_call"
                    @click="bookCall"
                />
            </div>
            <template v-else>
                <Step1
                    v-if="step === 1"
                    :data="data"
                    :errors="prefillErrors"
                    :show-notice="showStep1Notice"
                    @update:data="updateData"
                    @next="onStep1Next"
                />
                <Step2
                    v-else-if="step === 2"
                    :data="data"
                    :selected-country-translated="selectedCountryTranslated"
                    @update:data="updateData"
                    @back="back"
                    @calculate="next"
                />
                <!-- @get-started="getStarted" disabled — CFO Toolbox destination not finalised -->
                <Step3
                    v-else-if="step === 3"
                    :data="data"
                    :download-state="pdfCardDownloadState"
                    @update:data="updateData"
                    @back="back"
                    @restart="onRestart"
                    @book-call="bookCall"
                    @download="openPdfDownloadModal"
                />
            </template>
        </div>
    </div>
    <CalcPdfDocument
        v-if="step === 3 && pdfCalculationData"
        ref="pdfDocument"
        :calculation-data="pdfCalculationData"
        :locale="$i18n.locale"
    />
    <CalcPdfDownloadModal
        v-if="showPdfModal"
        :open="showPdfModal"
        @confirm="onPdfDownloadConfirmed"
        @close="showPdfModal = false"
    />
</template>

<script>
import CalcNav from './components/CalcNav.vue'
import Step1 from './views/Step1.vue'
import Step2 from './views/Step2.vue'
import Step3 from './views/Step3.vue'
import {
    parseUrlPrefill,
    validatePrefill,
    validateFinanceAmount,
    validateLendingTerm,
    validatePlannedUtilization,
    validateBillingUnit,
} from './services/prefill.js'
import CalcButton from './components/CalcButton.vue'
import CalcPdfDocument from './components/pdf/CalcPdfDocument.vue'
import CalcPdfDownloadModal from './components/CalcPdfDownloadModal.vue'
import { exportElementsToPdf } from './helpers/pdfExport.js'
import { calculateOffer } from './services/calc.js'
import { FULLY_DEPRECIATED_RESIDUAL } from './services/calcParams.js'
import { postPdfSession } from './services/api.js'
import { getBillingLabelKey } from './data/billingLabelKeys.js'
import { getCountryLabelKey } from './data/countryLabelKeys.js'
import * as Sentry from '@sentry/vue'

const EXTERNAL_LINKS = Object.freeze({
    landing: 'https://www.linxfour.com/',
    landingDe: 'https://www.linxfour.com/de',
    // cfoToolbox: '#', // disabled — destination not finalised
    bookCall: 'https://meetings.hubspot.com/julian-baier/initial-meeting',
})

const initialData = () => ({
    amount: null,
    lending: '',
    utilization: null,
    billing: '',
    country: 'DE',
    depreciation: true,
    residual: null,
    projectName: '',
})

const STEPPER_LABELS = [
    'Financing details',
    'Contract Information',
    'Calculation Preview',
]

export default {
    name: 'CalculatorApp',

    components: {
        CalcNav,
        Step1,
        Step2,
        Step3,
        CalcButton,
        CalcButton,
        CalcPdfDocument,
        CalcPdfDownloadModal,
    },

    data() {
        return {
            step: 1,
            maxReachedStep: 1,
            data: initialData(),
            prefillErrors: {},
            isExportingPdf: false,
            showPdfModal: false,
            pdfDoneFlash: false,
            pdfDoneFlashTimer: null,
            showStep1Notice: false,
            financingUnavailable: false,
            financingUnavailable: false,
        }
    },

    created() {
        const search =
            (typeof window !== 'undefined' &&
                window.location &&
                window.location.search) ||
            ''
        const { hasIntent, rawValues } = parseUrlPrefill(search)
        const { data, errors } = validatePrefill(rawValues)

        if (data.locale === 'de') this.$i18n.locale = 'de-DE'
        else if (data.locale === 'en') this.$i18n.locale = 'en-GB'

        // Seed the project name with the translated default file name
        // (without any .pdf extension — that's added only at download time).
        this.data.projectName = this.defaultProjectName()

        const financingCountries =
            (typeof window !== 'undefined' && Array.isArray(window.calcFinancingCountries)
                ? window.calcFinancingCountries
                : []) || []

        if (financingCountries.length === 0) {
            this.financingUnavailable = true
            Sentry.captureMessage(
                'Mini-calculator boot: empty financing-eligible country list',
                { level: 'warning' },
            )
            return
        }

        if (!financingCountries.some((c) => c && c.code === this.data.country)) {
            this.data = { ...this.data, country: financingCountries[0].code }
        }

        if (!hasIntent) return

        const toNumOrPassthrough = (v) => {
            if (v == null || v === '') return v
            const n = Number(v)
            return Number.isFinite(n) ? n : v
        }

        const wizardPatch = this.dropNullValues({
            amount: toNumOrPassthrough(data.amount),
            lending: data.lending,
            utilization: toNumOrPassthrough(data.utilization),
            billing: data.billing,
        })
        this.data = { ...this.data, ...wizardPatch }
        this.prefillErrors = errors

        if (Object.keys(errors).length === 0) {
            this.goTo(2)
        } else {
            this.showStep1Notice = true
        }
    },

    computed: {
        panel1Valid() {
            const d = this.data
            return (
                !validateFinanceAmount(d.amount).error &&
                !validateLendingTerm(d.lending).error &&
                !validatePlannedUtilization(d.utilization).error &&
                !validateBillingUnit(d.billing).error
            )
        },

        panel1RangeErrors() {
            const d = this.data
            const out = {}
            const checks = [
                ['amount', validateFinanceAmount(d.amount)],
                ['lending', validateLendingTerm(d.lending)],
                ['utilization', validatePlannedUtilization(d.utilization)],
                ['billing', validateBillingUnit(d.billing)],
            ]
            for (const [key, result] of checks) {
                if (result.error) out[key] = [result.error]
            }
            return out
        },

        selectedCountryTranslated() {
            const list =
                (typeof window !== 'undefined' && window.calcFinancingCountries) || []
            const countries = Array.isArray(list) ? list : []
            const match = countries.find((c) => c && c.code === this.data.country)
            if (!match) return ''
            const label =
                getCountryLabelKey(this.data.country, countries) ||
                match.name ||
                this.data.country
            return typeof label === 'string' && label.startsWith('§')
                ? this.$t(label)
                : label
        },

        stepperCurrent() {
            return this.step
        },

        stepperMaxReached() {
            return this.maxReachedStep
        },

        screenLabel() {
            const label = STEPPER_LABELS[this.step - 1] || ''
            return `0${this.step} ${label}`
        },

        pdfCalculationData() {
            const result = calculateOffer(this.data)
            if (!result) return null
            // The intro switches to a named-project variant when the user has
            // given the project a meaningful name (≥2 chars) that differs from
            // the seeded default.
            const projectName = (this.data.projectName || '').trim()
            const hasCustomProjectName =
                projectName.length >= 2 && projectName !== this.defaultProjectName()
            return {
                amount: this.data.amount,
                duration: Number(this.data.lending),
                expectedUtilization: this.data.utilization,
                billingUnit: this.data.billing,
                billingLabelKey: getBillingLabelKey(this.data.billing),
                country: this.data.country,
                countryTranslated: this.selectedCountryTranslated,
                projectName,
                hasCustomProjectName,
                depreciation: !!this.data.depreciation,
                residualValue: this.data.depreciation ? FULLY_DEPRECIATED_RESIDUAL : (this.data.residual ?? null),
                flex: {
                    low:    { baseFee: result.monthlyBaseFee.low,    perUsage: result.perUsage.low    },
                    medium: { baseFee: result.monthlyBaseFee.medium, perUsage: result.perUsage.medium },
                    high:   { baseFee: result.monthlyBaseFee.high,   perUsage: result.perUsage.high   },
                },
            }
        },

        pdfInputs() {
            if (!calculateOffer(this.data)) return null
            return {
                amount: this.data.amount,
                duration: Number(this.data.lending),
                expectedUtilization: this.data.utilization,
                residualValue: this.data.depreciation ? FULLY_DEPRECIATED_RESIDUAL : (this.data.residual ?? null),
                depreciation: !!this.data.depreciation,
                billingUnit: this.data.billing,
                country: this.data.country,
            }
        },

        pdfCalculations() {
            const result = calculateOffer(this.data)
            if (!result) return null
            return {
                flexLowBaseFee:     result.monthlyBaseFee.low,
                flexLowPerUsage:    result.perUsage.low,
                flexMediumBaseFee:  result.monthlyBaseFee.medium,
                flexMediumPerUsage: result.perUsage.medium,
                flexHighBaseFee:    result.monthlyBaseFee.high,
                flexHighPerUsage:   result.perUsage.high,
            }
        },

        pdfCardDownloadState() {
            if (this.showPdfModal || this.isExportingPdf) return 'downloading'
            if (this.pdfDoneFlash) return 'done'
            return 'idle'
        },
    },

    methods: {
        dropNullValues(obj) {
            return Object.fromEntries(
                Object.entries(obj || {}).filter(([, v]) => v != null),
            )
        },

        defaultProjectName() {
            const translated = this.$t('§calc.step3.pdf.filename')
            return (translated || '').replace(/\.pdf$/i, '')
        },

        updateData(patch) {
            this.data = { ...this.data, ...(patch || {}) }
            if (
                patch &&
                this.prefillErrors &&
                Object.keys(this.prefillErrors).length > 0
            ) {
                const errs = { ...this.prefillErrors }
                let changed = false
                for (const k of Object.keys(patch)) {
                    if (errs[k]) {
                        delete errs[k]
                        changed = true
                    }
                }
                if (changed) this.prefillErrors = errs
            }
        },

        goTo(n) {
            this.step = n
            if (n >= 1 && n <= 3) {
                this.maxReachedStep = Math.max(this.maxReachedStep, n)
            }
        },

        next() {
            this.goTo(Math.min(3, this.step + 1))
        },

        back() {
            if (this.step === 2) this.goTo(1)
            else if (this.step === 3) this.goTo(2)
        },

        restart() {
            this.data = initialData()
            this.data.projectName = this.defaultProjectName()
            this.step = 1
            this.maxReachedStep = 1
            this.prefillErrors = {}
        },

        onRestart() {
            this.restart()
            this.goTo(1)
        },

        onStep1Next() {
            if (this.panel1Valid) {
                this.showStep1Notice = false
                this.prefillErrors = {}
                this.goTo(2)
            } else {
                this.showStep1Notice = true
                this.prefillErrors = { ...this.prefillErrors, ...this.panel1RangeErrors }
            }
        },

        onStepperClick(idx) {
            if (idx > this.maxReachedStep) return
            this.goTo(idx)
        },

        exit() {
            window.location.href =
                this.$i18n.locale === 'de-DE'
                    ? EXTERNAL_LINKS.landingDe
                    : EXTERNAL_LINKS.landing
        },

        // getStarted() {
        //     window.location.href = EXTERNAL_LINKS.cfoToolbox
        // },

        bookCall() {
            window.open(EXTERNAL_LINKS.bookCall, '_blank')
        },

        openPdfDownloadModal() {
            if (this.pdfInputs == null) return
            this.showPdfModal = true
        },

        async onPdfDownloadConfirmed({ captchaToken }) {
            const inputs = this.pdfInputs
            const calculations = this.pdfCalculations
            if (!inputs || !calculations) {
                this.showPdfModal = false
                return
            }
            try {
                await postPdfSession({
                    inputs,
                    calculations,
                    locale: this.$i18n?.locale || 'en-GB',
                    captchaToken,
                })
                await this.runPdfDownload()
            } catch (e) {
                Sentry.captureException(e, {
                    tags: {
                        operation: 'pdf-session',
                        locale: this.$i18n?.locale || 'en-GB',
                    },
                    extra: {
                        inputs,
                    },
                })
            } finally {
                this.showPdfModal = false
            }
        },

        async runPdfDownload() {
            if (this.isExportingPdf) return
            this.isExportingPdf = true
            let didSucceed = false
            try {
                await this.$nextTick()
                await new Promise((resolve) =>
                    requestAnimationFrame(() => requestAnimationFrame(resolve)),
                )

                const locale = this.$i18n?.locale || 'en-GB'
                const fallbackKey = '§calc.step3.pdf.filename'
                const translated = this.$t ? this.$t(fallbackKey) : fallbackKey
                const generic = `Linxfour-Calculation-${locale}`

                // Prefer the user's project name (min 2 chars); otherwise fall back to the translated default file name
                const userName = (this.data.projectName || '').trim()
                const translatedName =
                    translated && translated !== fallbackKey ? translated : ''
                const candidate = userName.length >= 2 ? userName : translatedName
                const rawBase =
                    candidate && candidate !== fallbackKey ? candidate : generic

                const safeBase =
                    rawBase
                        // eslint-disable-next-line no-control-regex
                        .replace(/[\\/:*?"<>|\u0000-\u001F\u007F-\u009F]+/g, '-')
                        .replace(/\s+/g, ' ')
                        .trim() || generic

                const filename = /\.pdf$/i.test(safeBase)
                    ? safeBase
                    : `${safeBase}.pdf`

                const pages = this.$refs.pdfDocument.getPageElements()
                const blob = await exportElementsToPdf(pages)
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                a.download = filename
                document.body.appendChild(a)
                a.click()
                document.body.removeChild(a)
                setTimeout(() => URL.revokeObjectURL(url), 0)
                didSucceed = true
            } catch (e) {
                Sentry.captureException(e, {
                    tags: {
                        operation: 'pdf-export',
                        locale: this.$i18n?.locale || 'en-GB',
                    },
                    extra: {
                        inputs: this.pdfInputs,
                    },
                })
            } finally {
                this.isExportingPdf = false
                if (didSucceed) {
                    if (this.pdfDoneFlashTimer) clearTimeout(this.pdfDoneFlashTimer)
                    this.pdfDoneFlash = true
                    this.pdfDoneFlashTimer = setTimeout(() => {
                        this.pdfDoneFlash = false
                        this.pdfDoneFlashTimer = null
                    }, 1100)
                }
            }
        },
    },

    beforeUnmount() {
        if (this.pdfDoneFlashTimer) {
            clearTimeout(this.pdfDoneFlashTimer)
            this.pdfDoneFlashTimer = null
        }
    },
}
</script>

<style scoped>
.calc-shell {
    position: relative;
    min-height: 100vh;
    width: 100%;
    overflow: hidden;
    background: var(--calc-green-base);
}

.calc-shell__bg {
    position: absolute;
    inset: 0;
    z-index: 0;
    background: url('/calculator-assets/fe_background.png') no-repeat center
        center / cover;
    pointer-events: none;
}

.calc-stage {
    position: relative;
    z-index: 1;
    padding: 54px 24px 80px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
}

@media (max-width: 760px) {
    .calc-stage {
        padding: 16px 16px 56px;
        gap: 16px;
    }
}

.calc-unavailable {
    max-width: 560px;
    width: 100%;
    margin: 40px auto 0;
    padding: 32px 28px;
    background: var(--calc-card-bg, #fff);
    border-radius: 16px;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
}

.calc-unavailable__title {
    font-size: 22px;
    line-height: 1.25;
    margin: 0;
    color: var(--calc-ink, #14241e);
}

.calc-unavailable__body {
    font-size: 15px;
    line-height: 1.5;
    margin: 0 0 8px;
    color: var(--calc-ink, #14241e);
    opacity: 0.75;
}

.calc-unavailable {
    max-width: 560px;
    width: 100%;
    margin: 40px auto 0;
    padding: 32px 28px;
    background: var(--calc-card-bg, #fff);
    border-radius: 16px;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
}

.calc-unavailable__title {
    font-size: 22px;
    line-height: 1.25;
    margin: 0;
    color: var(--calc-ink, #14241e);
}

.calc-unavailable__body {
    font-size: 15px;
    line-height: 1.5;
    margin: 0 0 8px;
    color: var(--calc-ink, #14241e);
    opacity: 0.75;
}
</style>
